import {
  FontWeight,
  formatTimeFromMinutes,
  IconLock,
  IconX,
  pluralize,
  Select,
  type SelectOption,
} from '@leland-dev/leland-ui-library';
import React, { type FC, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { SkuFamily } from '../../../../__generated-gql-types__/globalTypes';
import { LexSlug, useLex } from '../../../../context/LexContext';
import { type InputOption, Radio } from '../../../forms/Inputs';
import { SessionOption } from '../ScheduleModal';

import { RecurringSessionSelect } from './RecurringSessionSelect';
import {
  type ScheduleModalSidebarCoachFragment,
  type ScheduleModalSidebarCoachingSessionFragment,
  type ScheduleModalSidebarOrderFragment,
  type ScheduleModalSidebarRecurringCadenceFragment,
  useScheduleModalSidebarUserQuery,
} from './__generated-gql-types__/ScheduleModalSidebar.generated';

export const sessionLengthToLabelValue = {
  15: '15 minutes',
  30: '30 minutes',
  45: '45 minutes',
  60: '1 hour',
  75: '1 hour 15 minutes',
  90: '1 hour 30 minutes',
  105: '1 hour 45 minutes',
  120: '2 hours',
};

const LIMIT_WARNING_THRESHOLD = 2;

interface ScheduleModalSidebarProps {
  coach: ScheduleModalSidebarCoachFragment;
  coachingSession?: Possible<ScheduleModalSidebarCoachingSessionFragment>;
  hasUnlimitedBookingPower: boolean;
  isReschedule?: boolean;
  locked?: boolean;
  onChangeSessionLength?: (length: number) => void;
  onCloseModal: (open: boolean) => void;
  order: Possible<ScheduleModalSidebarOrderFragment>;
  sessionLength: number;
  sessionType?: SessionOption;
  setSessionType?: (type: SessionOption) => void;
  sessionPrice?: number;
  selectedTime: Possible<number>;
  recurringCadence: Possible<ScheduleModalSidebarRecurringCadenceFragment>;
  setRecurringCadence: (
    recurringCadence: Possible<ScheduleModalSidebarRecurringCadenceFragment>,
  ) => void;
}

export const ScheduleModalSidebar: FC<ScheduleModalSidebarProps> = ({
  coach,
  coachingSession,
  hasUnlimitedBookingPower,
  isReschedule,
  locked,
  onChangeSessionLength,
  onCloseModal,
  sessionLength,
  sessionType,
  setSessionType,
  selectedTime,
  recurringCadence,
  setRecurringCadence,
}) => {
  const { data: userData, loading: userDataLoading } =
    useScheduleModalSidebarUserQuery();
  const { isLexEnabled } = useLex();

  const isLinkedInTrial =
    userData?.user?.applicant?.hasUnassignedTrialOrder ||
    coach.coachingRelationship?.customerSubscription?.sku.family ===
      SkuFamily.LINKEDIN;

  const isReschedulingIntroCall =
    !!isReschedule &&
    !!coachingSession &&
    coachingSession.content.__typename === 'OneOnOneSession' &&
    !!coachingSession.content.introOrder?.introCall;

  const couldScheduleIntro =
    isReschedulingIntroCall ||
    (coach.couldScheduleIntro &&
      !userData?.user?.applicant?.blockIntroUntil &&
      !isLinkedInTrial);

  const introSummary = userData?.user?.applicant?.introSummary;
  // introSummary?.hasIntroQuota is the same as introSummary.numOfBetweenPurchaseIntroRemaining > 0 && introSummary.numOfDailyIntroRemaining > 0
  const hasIntroQuota =
    !!introSummary?.hasIntroQuota || isReschedulingIntroCall;

  const couldScheduleIntroAndHasQuota = couldScheduleIntro && hasIntroQuota;

  // Whether to show warning of low quota or no more intro calls
  const showIntroLimitWarning =
    !coach.coachingRelationship &&
    !isReschedule &&
    couldScheduleIntro &&
    introSummary &&
    (introSummary.numOfBetweenPurchaseIntroRemaining <=
      LIMIT_WARNING_THRESHOLD ||
      introSummary.numOfDailyIntroRemaining <= LIMIT_WARNING_THRESHOLD);

  const sessionTypesToShow = useMemo(() => {
    if (userDataLoading) {
      return [];
    }
    return [
      ...((locked && sessionType === SessionOption.COACHING_SESSION) ||
      !couldScheduleIntroAndHasQuota
        ? []
        : [SessionOption.INTRO_CALL]),
      ...(locked && sessionType === SessionOption.INTRO_CALL
        ? []
        : [SessionOption.COACHING_SESSION]),
    ];
  }, [locked, sessionType, couldScheduleIntroAndHasQuota, userDataLoading]);

  const { register, watch, setValue } = useForm<{
    sessionType: SessionOption;
  }>({
    defaultValues: {
      sessionType:
        coach.coachingRelationship || !couldScheduleIntroAndHasQuota
          ? SessionOption.COACHING_SESSION
          : SessionOption.INTRO_CALL,
    },
  });

  useEffect(() => {
    if (sessionTypesToShow.length === 0) {
      return;
    }
    if (sessionType && sessionTypesToShow.includes(sessionType)) {
      setValue('sessionType', sessionType);
    } else {
      // Override the passed in session type if it's not in the list of session types to show
      const sessionTypeToUse = sessionTypesToShow[0];
      setValue('sessionType', sessionTypeToUse);
      setSessionType?.(sessionTypeToUse);
    }
  }, [sessionType, setSessionType, setValue, sessionTypesToShow]);

  const { availableTime = 0 } = coach.coachingTimeSummary ?? {};

  const sessionLengthOptions: Record<
    SessionOption,
    Array<SelectOption<number>>
  > = {
    [SessionOption.INTRO_CALL]: [
      { value: 15, label: sessionLengthToLabelValue[15] },
    ],
    [SessionOption.COACHING_SESSION]: Object.entries(
      sessionLengthToLabelValue,
    ).map(([value, label]) => ({
      value: Number(value),
      label,
    })),
  };

  const formatOptionLabel = (title: string, subtitle: string) => (
    <div className="ml-3 flex grow justify-between text-left text-leland-gray-dark">
      <div className="flex flex-col">
        <span className="font-medium">{title}</span>
        <span className="text-leland-gray-light">{subtitle}</span>
      </div>
    </div>
  );

  const sessionTypeOptions: InputOption[] = sessionTypesToShow.map((type) => ({
    label:
      type === SessionOption.INTRO_CALL
        ? formatOptionLabel(
            'Book a free intro call',
            `Explore ${coach.user.firstName}’s coaching offerings`,
          )
        : formatOptionLabel(
            'Book a coaching session',
            `Work with ${coach.user.firstName} to achieve your goals`,
          ),
    value: type,
  }));

  const watchedSessionType = watch('sessionType');

  const registeredSessionType = register('sessionType');

  const isHourlySessionDisabled =
    watchedSessionType === SessionOption.INTRO_CALL;

  if (!userData || userDataLoading) {
    return null;
  }

  return (
    <div className="relative flex size-full flex-col border-b border-leland-gray-stroke p-6 md:overflow-auto md:overscroll-contain md:border-b-0 md:border-r md:pb-40">
      <IconX
        className="absolute right-6 top-6 size-6 text-leland-gray-light md:hidden"
        onClick={() => onCloseModal(false)}
      />
      <h1 className="text-3xl font-medium text-leland-gray-dark md:mt-6">
        {isReschedule
          ? 'Reschedule your session.'
          : coach.everHadCoachingSessionWithCoach
            ? 'Book your next session to keep making progress.'
            : 'Take the first step toward achieving your goals.'}
      </h1>
      {isReschedule || sessionTypesToShow.length === 1 ? null : (
        <p className="mt-1 border-leland-gray-stroke text-lg text-leland-gray-light">
          Select a session type below.
        </p>
      )}
      <div className="mt-6 w-full divide-y divide-leland-gray-stroke rounded-lg border border-leland-gray-stroke">
        <Radio
          fullWidth
          labelIsHidden
          className="divide-y divide-leland-gray-stroke"
          labelClassName="p-5 w-full"
          label="Select an option"
          options={sessionTypeOptions}
          register={registeredSessionType}
          hideRadioWhenSingleOption
          onChange={(e) => {
            setSessionType?.(
              (e.target as HTMLInputElement).value as SessionOption,
            );
            return registeredSessionType.onChange(e);
          }}
        />
        {!hasUnlimitedBookingPower && availableTime > 0 ? (
          <div className="w-full rounded-b-lg bg-leland-gray-hover p-2">
            <p className="text-leland-gray-light">
              {formatTimeFromMinutes(availableTime)} available for scheduling
            </p>
          </div>
        ) : null}
      </div>
      <div className="mt-6 w-full space-y-2">
        <Select
          label=""
          value={sessionLength}
          disabled={isHourlySessionDisabled}
          onValueChange={(value: number) => onChangeSessionLength?.(value)}
          options={sessionLengthOptions[watchedSessionType]}
          fontWeight={FontWeight.MEDIUM}
          DisabledIcon={IconLock}
          disabledTooltipContent="To meet for longer than 15 minutes, book a coaching session."
        />
        {isLexEnabled(LexSlug.RECURRING_SESSIONS) &&
        (coachingSession?.recurringSession || !isReschedule) ? (
          <RecurringSessionSelect
            value={recurringCadence}
            onChange={setRecurringCadence}
            startAt={new Date(selectedTime ?? 0)}
            durationMinutes={sessionLength}
            disabled={isHourlySessionDisabled}
          />
        ) : null}
      </div>
      {showIntroLimitWarning ? (
        <div className="mt-6 w-full rounded bg-leland-gray-hover p-3">
          <p className="text-leland-gray-light">
            {introSummary.numOfBetweenPurchaseIntroRemaining === 0
              ? 'To access additional intro calls, you’ll need to make a purchase.'
              : introSummary.numOfDailyIntroRemaining === 0
                ? 'You can’t schedule any more intro calls today.'
                : introSummary.numOfBetweenPurchaseIntroRemaining <=
                    LIMIT_WARNING_THRESHOLD
                  ? `You have ${introSummary.numOfBetweenPurchaseIntroRemaining} more intro
            ${pluralize(
              introSummary.numOfBetweenPurchaseIntroRemaining,
              'call',
              {
                excludeNumber: true,
              },
            )}
            available. To access additional intro calls, you’ll need to make a purchase.`
                  : `You can schedule ${introSummary.numOfDailyIntroRemaining} more intro
            ${pluralize(introSummary.numOfDailyIntroRemaining, 'call', {
              excludeNumber: true,
            })}
            today.`}
          </p>
        </div>
      ) : null}
    </div>
  );
};
